import React, { useEffect, useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  ArrowLeft, 
  Trash2, 
  Plus, 
  Minus,
  ShoppingBag,
  CreditCard,
  Loader2
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import ShippingCalculator from '../components/ShippingCalculator';
import { trackInitiateCheckout } from '../utils/fbpixel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Cart = () => {
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(false);
  const [selectedShipping, setSelectedShipping] = useState(null);

  useEffect(() => {
    loadCart();
  }, []);

  const loadCart = () => {
    try {
      const cart = JSON.parse(localStorage.getItem('cart') || '[]');
      setCartItems(cart);
    } catch (error) {
      console.error('Error loading cart:', error);
      toast.error('Failed to load cart');
    } finally {
      setLoading(false);
    }
  };

  const updateCart = (newCart) => {
    localStorage.setItem('cart', JSON.stringify(newCart));
    setCartItems(newCart);
  };

  const removeItem = (productId, customUrl = '') => {
    const newCart = cartItems.filter(item => !(item.id === productId && item.custom_url === customUrl));
    updateCart(newCart);
    toast.success('Item removed from cart');
  };

  const updateQuantity = (productId, newQuantity, customUrl = '') => {
    if (newQuantity < 1) return;
    
    const newCart = cartItems.map(item => 
      item.id === productId && item.custom_url === customUrl ? { ...item, quantity: newQuantity } : item
    );
    updateCart(newCart);
  };

  const calculateSubtotal = () => {
    return cartItems.reduce((total, item) => {
      const price = item.sale_price || item.price;
      return total + (price * item.quantity);
    }, 0);
  };

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    const shippingCost = selectedShipping?.price || 0;
    return subtotal + shippingCost;
  };

  const handleShippingSelected = (shippingOption) => {
    setSelectedShipping(shippingOption);
  };

  const handleCheckout = async () => {
    if (cartItems.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    if (!selectedShipping) {
      toast.error('Please select a shipping option');
      return;
    }

    try {
      setCheckingOut(true);

      // Prepare cart items for checkout
      const checkoutItems = cartItems.map(item => ({
        id: item.id,
        title: item.title,
        price: item.sale_price || item.price,
        quantity: item.quantity,
        image_url: item.images?.[0] || null,
        custom_url: item.custom_url || null  // Include NFC programming URL
      }));

      // Meta Pixel — InitiateCheckout (before redirect to Stripe, after validation)
      trackInitiateCheckout({
        cartTotal: calculateTotal(),
        numItems: cartItems.reduce((sum, i) => sum + (i.quantity || 1), 0),
        contentIds: cartItems.map((i) => i.id),
      });

      // Get origin URL
      const originUrl = window.location.origin;

      // Create checkout session with shipping
      const response = await axios.post(`${API_URL}/api/stripe/create-checkout-session`, {
        cart_items: checkoutItems,
        customer_email: null,
        origin_url: originUrl,
        shipping: {
          method: selectedShipping.method,
          carrier: selectedShipping.carrier,
          cost: selectedShipping.price,
          estimated_days: selectedShipping.estimated_days
        }
      });

      // Redirect to Stripe checkout
      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.detail || 'Failed to initiate checkout');
      setCheckingOut(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-blue-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <header className="border-b border-zinc-800 bg-black/80 backdrop-blur-xl sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/products')}
              className="text-gray-300 hover:text-white"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Continue Shopping
            </Button>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <ShoppingBag className="w-6 h-6" />
              Shopping Cart
            </h1>
            <div className="w-40"></div> {/* Spacer for centering */}
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-12">
        {cartItems.length === 0 ? (
          // Empty Cart State
          <div className="text-center py-20">
            <ShoppingBag className="w-24 h-24 text-gray-600 mx-auto mb-6" />
            <h2 className="text-3xl font-bold text-white mb-4">Your cart is empty</h2>
            <p className="text-gray-400 mb-8">Start designing your custom NFC products!</p>
            <Button 
              onClick={() => navigate('/products')}
              className="bg-blue-500 hover:bg-blue-600 text-white"
            >
              Browse Products
            </Button>
          </div>
        ) : (
          // Cart with Items
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-4">
              {cartItems.map((item) => {
                const price = item.sale_price || item.price;
                return (
                  <Card key={item.id} className="bg-gray-900 border-gray-800">
                    <CardContent className="p-6">
                      <div className="flex gap-6">
                        {/* Product Image */}
                        <div className="w-24 h-24 bg-gray-800 rounded-lg overflow-hidden flex-shrink-0">
                          <img
                            src={item.images?.[0] || 'https://via.placeholder.com/100'}
                            alt={item.title}
                            className="w-full h-full object-cover"
                          />
                        </div>

                        {/* Product Info */}
                        <div className="flex-1">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <h3 className="text-lg font-semibold text-white">{item.title}</h3>
                              <p className="text-sm text-gray-400">{item.category}</p>
                              {item.custom_url && (
                                <div className="mt-2 bg-blue-500/10 border border-blue-500/30 rounded px-3 py-2">
                                  <p className="text-xs text-gray-400 mb-1">NFC Programmed URL:</p>
                                  <p className="text-sm text-blue-400 break-all">{item.custom_url}</p>
                                </div>
                              )}
                            </div>
                            <button
                              onClick={() => removeItem(item.id, item.custom_url)}
                              className="text-red-500 hover:text-red-400 p-2"
                              title="Remove item"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>

                          <div className="flex justify-between items-end">
                            {/* Quantity Controls */}
                            <div className="flex items-center gap-3 bg-gray-800 rounded-lg p-1">
                              <button
                                onClick={() => updateQuantity(item.id, item.quantity - 1, item.custom_url)}
                                className="p-2 hover:bg-gray-700 rounded transition-colors"
                                disabled={item.quantity <= 1}
                              >
                                <Minus className="w-4 h-4 text-gray-300" />
                              </button>
                              <span className="text-white font-medium w-8 text-center">
                                {item.quantity}
                              </span>
                              <button
                                onClick={() => updateQuantity(item.id, item.quantity + 1, item.custom_url)}
                                className="p-2 hover:bg-gray-700 rounded transition-colors"
                              >
                                <Plus className="w-4 h-4 text-gray-300" />
                              </button>
                            </div>

                            {/* Price */}
                            <div className="text-right">
                              <div className="text-xl font-bold text-white">
                                ${(price * item.quantity).toFixed(2)}
                              </div>
                              {item.sale_price && (
                                <div className="text-sm text-gray-500 line-through">
                                  ${(item.price * item.quantity).toFixed(2)}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1 space-y-6">
              {/* Shipping Calculator */}
              <ShippingCalculator 
                cartItems={cartItems}
                subtotal={calculateSubtotal()}
                onShippingSelected={handleShippingSelected}
              />

              {/* Order Total */}
              <Card className="bg-gray-900 border-gray-800 sticky top-24">
                <CardContent className="p-6">
                  <h2 className="text-2xl font-bold text-white mb-6">Order Summary</h2>
                  
                  <div className="space-y-3 mb-6">
                    <div className="flex justify-between text-gray-300">
                      <span>Subtotal ({cartItems.length} items)</span>
                      <span>${calculateSubtotal().toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-gray-300">
                      <span>Shipping</span>
                      <span>
                        {selectedShipping 
                          ? (selectedShipping.price === 0 
                              ? 'FREE' 
                              : `$${selectedShipping.price.toFixed(2)}`)
                          : 'Select shipping'
                        }
                      </span>
                    </div>
                    <div className="h-px bg-gray-800 my-4"></div>
                    <div className="flex justify-between text-xl font-bold text-white">
                      <span>Total</span>
                      <span>${calculateTotal().toFixed(2)}</span>
                    </div>
                  </div>

                  <Button
                    onClick={handleCheckout}
                    disabled={checkingOut || !selectedShipping}
                    className="w-full bg-blue-500 hover:bg-blue-600 text-white py-6 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {checkingOut ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <CreditCard className="w-5 h-5 mr-2" />
                        Proceed to Checkout
                      </>
                    )}
                  </Button>

                  <p className="text-xs text-gray-500 text-center mt-4">
                    Secure checkout powered by Stripe
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Cart;
